import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { useAppDispatch, useAppSelector } from "lib/hooks"
import { categorySelectors } from "reducers/categories"
import { QueueConversation } from "types"

import { BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ConfirmDialog from "components/v2/conversationList/components/ConfirmDialog/ConfirmDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

import { claimOverviewConversation } from "../../../../api"

interface ActiveConversationListProps {
  title: string
  conversations: QueueConversation[]
}

export interface ActiveConversationListItem extends ConversationListItem<QueueConversation> {}

const ActiveConversationList = ({ title, conversations }: ActiveConversationListProps) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const categories = useAppSelector(categorySelectors.selectEntities)
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<QueueConversation>()
  const openClaimConversationDialog = (item: ActiveConversationListItem) => {
    setSelectedConversation(item.conversation)
    setClaimDialogOpen(true)
  }
  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "category",
      name: intl.formatMessage({ id: "label.overview.reason_for_visit" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.errand_started" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: QueueConversation): ActiveConversationListItem => {
      const isChildConversation = conv.profile?.type === "child"
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          category: categories[conv.categoryId].name,
          start: <DateTimePillCell value={conv.queuePriority} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {
            categories
          }),
          category: sortValueFunctions.category(conv, { categories }),
          start: sortValueFunctions.queuePriority(conv)
        }
      }
    },
    [categories]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const claimQueueConversation = () => {
    if (selectedConversation) {
      dispatch(claimOverviewConversation(selectedConversation))
    }
  }

  return (
    <>
      <ConversationTable
        title={title}
        columns={columns}
        items={list}
        emptyMessage={intl.formatMessage({ id: "label.overview.queue_empty" })}
        onItemClick={openClaimConversationDialog}
      />
      <ConfirmDialog
        isOpen={claimDialogOpen}
        title={intl.formatMessage({ id: "conversation.claim.confirm.dialog.title" })}
        message={intl.formatMessage({ id: "conversation.claim.confirm.dialog.message" })}
        conversation={selectedConversation}
        onConfirm={claimQueueConversation}
        onClose={() => setClaimDialogOpen(false)}
        confirmText={intl.formatMessage({ id: "conversation.claim.confirm.dialog.button" })}
      />
    </>
  )
}

export default ActiveConversationList
